import { Favorite, Hymn } from '../../../common/src/model.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { ensureExists } from '../../../common/src/util.ts';
import { LocalStorageKey } from './client_local_storage.ts';

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { downloadFavorites } from '../util/music_cache_refresh.ts';
import { userIsSignedIn } from '../util/shared.tsx';
import { useDownloads } from '../util/use_downloads.tsx';
import { useOnline } from '../util/use_online.tsx';
import { HymnalsContext } from './use_hymnals.tsx';
import { useLocalStorage } from './use_local_storage.tsx';

const favoritesSyncIntervalMs = 5000; // only while Favorites page is visible

export interface FavoritesContextProps {
  favoriteHymns: Hymn[];
  addFavorite: (hymn: Hymn) => void;
  removeFavorite: (hymn: Hymn) => void;
  includesHymn: (hymn: Hymn) => boolean;
  setEnableServerSync: Dispatch<SetStateAction<boolean>>;
}

export const FavoritesContext = createContext<FavoritesContextProps>({} as FavoritesContextProps);

export const FavoritesContextProvider = ({ children }: { children: ReactNode }) => {
  const [server, setServer] = useLocalStorage(LocalStorageKey.Favorites);
  const [added, setAdded] = useLocalStorage(LocalStorageKey.FavoritesAdded);
  const [removed, setRemoved] = useLocalStorage(LocalStorageKey.FavoritesRemoved);
  const hymnals = use(HymnalsContext);
  const isOnline = useOnline();
  const { add: addDownload } = useDownloads();
  const [enableServerSync, setEnableServerSync] = useState(false);

  const favorites = useMemo(
    () => [...server.filter((favorite) => !includesFavorite(removed, favorite)), ...added],
    [added, removed, server],
  );

  const favoriteHymns = useMemo(
    () =>
      favorites
        .map(({ hymnalName, songNumber }) =>
          hymnals.find(({ name }) => name === hymnalName)?.hymns?.find((hymn) => hymn.songNumber === songNumber),
        )
        .filter((hymn) => hymn !== undefined) as Hymn[],
    [favorites, hymnals],
  );

  const reconcileWithServer = useCallback(async () => {
    if (!userIsSignedIn() || !isOnline) {
      return;
    }
    for (const favorite of added) {
      await server_api.addFavorite(favorite);
    }
    for (const favorite of removed) {
      await server_api.removeFavorite(favorite);
    }
    setServer(await server_api.getFavorites());
    setAdded([]);
    setRemoved([]);

    // TODO(hewitt): Redownloading all favorites whenever a favorite is added or removed is expensive
    await downloadFavorites(addDownload);
  }, [addDownload, added, isOnline, removed, setAdded, setRemoved, setServer]);

  useEffect(() => {
    if (!enableServerSync) {
      return;
    }
    // TODO(hewitt): Use a socket to get notification that server favorites have changed
    void reconcileWithServer();
    const id = setInterval(reconcileWithServer, favoritesSyncIntervalMs);
    return () => clearInterval(id);
  }, [enableServerSync, reconcileWithServer]);

  const addFavorite = useCallback(
    (hymn: Hymn) => {
      const favorite: Favorite = {
        hymnalName: ensureExists(hymn.hymnal),
        songNumber: hymn.songNumber,
      };

      if (includesFavorite(removed, favorite)) {
        setRemoved(removed.filter((value) => !favoritesEqual(value, favorite)));
      } else {
        if (includesFavorite(added, favorite) || includesFavorite(server, favorite)) {
          console.error(`Already a favorite (cannot be added): ${favorite}`);
          return;
        }
        setAdded([...added, favorite]);
      }
      // reconcileWithServer will be called by react
    },
    [added, removed, server, setAdded, setRemoved],
  );

  const removeFavorite = useCallback(
    (hymn: Hymn) => {
      const favorite: Favorite = {
        hymnalName: ensureExists(hymn.hymnal),
        songNumber: hymn.songNumber,
      };

      if (includesFavorite(added, favorite)) {
        setAdded(added.filter((value) => !favoritesEqual(value, favorite)));
      } else {
        if (includesFavorite(removed, favorite) || !includesFavorite(server, favorite)) {
          console.error(`Not yet a favorite (cannot be removed): ${favorite}`);
          return;
        }
        setRemoved([...removed, favorite]);
      }
      // reconcileWithServer will be called by react
    },
    [added, removed, server, setAdded, setRemoved],
  );

  const includesHymn = useCallback(
    (hymn: Hymn): boolean => {
      const favorite: Favorite = {
        hymnalName: ensureExists(hymn.hymnal),
        songNumber: hymn.songNumber,
      };
      return includesFavorite(favorites, favorite);
    },
    [favorites],
  );

  return (
    <FavoritesContext value={{ favoriteHymns, addFavorite, removeFavorite, includesHymn, setEnableServerSync }}>
      {children}
    </FavoritesContext>
  );
};

function includesFavorite(list: Favorite[], favorite: Favorite) {
  for (const current of list) {
    if (favoritesEqual(current, favorite)) {
      return true;
    }
  }
  return false;
}

function favoritesEqual(lhs: Favorite, rhs: Favorite) {
  return lhs.hymnalName === rhs.hymnalName && lhs.songNumber === rhs.songNumber;
}
